import { useEffect, useState } from 'react';
import {
    Badge,
    BlockStack,
    Box,
    Button,
    Card,
    InlineStack,
    Select,
    Spinner,
    Typography,
} from '@pv-frontend/ui-components-web';
import { formatUkAndUtc } from './format-date';
import { HomeFeedPreferences } from './home-feed-preferences';
import { HomeFeedQuestions } from './home-feed-questions';
import { HomeFeedInferences, HomeFeedWidening } from './home-feed-offers';
import { HomeFeedEngagement, HomeFeedResultsChanged, HomeFeedWildcards } from './home-feed-activity';
import {
    resolveSelection,
    type HomeFeedHouseholdOutcome,
    type HomeFeedHouseholdView,
    type HomeFeedInspectionOutcome,
} from './home-feed-view';

/** Runs one dry-run inspection; the screen binds it to the `homeFeedInspection` query. */
export type InspectHomeFeed = (memberAccountId: string, searchId: string) => Promise<HomeFeedInspectionOutcome>;

export interface HomeFeedPanelProps {
    household: HomeFeedHouseholdOutcome;
    inspect: InspectHomeFeed;
    /** Refetches the household directory (flag state, members, Searches). */
    onReloadHousehold?: () => void;
    /** The last household reload failed; the household on screen is the previous one. */
    reloadFailed?: boolean;
    /** Vault members' emails by account id, for readable member options (the directory carries none). */
    memberEmails?: ReadonlyMap<string, string>;
}

type Inspecting = { kind: 'loading' } | HomeFeedInspectionOutcome;

function PanelCard({ children }: { children: React.ReactNode }) {
    return (
        <BlockStack gap="300">
            <Typography variant="heading-sm" as="h2">
                Home feed
            </Typography>
            <Card padding="0" testID="home-feed">
                <Box padding="400">{children}</Box>
            </Card>
        </BlockStack>
    );
}

function ReloadError({ onRetry }: { onRetry?: () => void }) {
    return (
        <InlineStack gap="200" blockAlign="center" wrap>
            <Typography variant="body-sm" tone="text-secondary" testID="home-feed-reload-error">
                Couldn&apos;t reload the household. Showing the last one loaded.
            </Typography>
            <Button variant="tertiary" size="slim" onPress={() => onRetry?.()}>
                Retry
            </Button>
        </InlineStack>
    );
}

function Note({ children, testID }: { children: string; testID: string }) {
    return (
        <Typography variant="body-md" tone="text-secondary" testID={testID}>
            {children}
        </Typography>
    );
}

/**
 * The Home feed section of vault detail (PRD pv-platform#156, slice 2): what the Home feed knows about
 * one member's view of one Search, worked out now by the feed's own decision code. Read-only. The panel
 * owns only the operator's pick and the refresh; the inspection itself is the screen's (`inspect`).
 * The tracer shows the flag state and "evaluated at"; later slices add the sections.
 */
export function HomeFeedPanel({ household, inspect, onReloadHousehold, reloadFailed, memberEmails }: HomeFeedPanelProps) {
    if (household.kind === 'loading') {
        return (
            <PanelCard>
                <InlineStack gap="200" blockAlign="center">
                    <Spinner size="small" />
                    <Note testID="home-feed-loading">Loading the household…</Note>
                </InlineStack>
            </PanelCard>
        );
    }
    if (household.kind === 'not-found') {
        return (
            <PanelCard>
                <Note testID="home-feed-not-found">
                    The Home feed inspector found no household for this vault.
                </Note>
            </PanelCard>
        );
    }
    if (household.kind === 'error') {
        return (
            <PanelCard>
                <Note testID="home-feed-error">
                    Couldn&apos;t load the household for the Home feed inspector. Only a named operator can
                    use it; reload to try again.
                </Note>
            </PanelCard>
        );
    }
    return (
        <HouseholdInspector
            household={household.household}
            inspect={inspect}
            onReloadHousehold={onReloadHousehold}
            reloadFailed={reloadFailed}
            memberEmails={memberEmails}
        />
    );
}

function HouseholdInspector({
    household,
    inspect,
    onReloadHousehold,
    reloadFailed,
    memberEmails,
}: {
    household: HomeFeedHouseholdView;
    inspect: InspectHomeFeed;
    onReloadHousehold?: () => void;
    reloadFailed?: boolean;
    memberEmails?: ReadonlyMap<string, string>;
}) {
    const [picked, setPicked] = useState<{ memberAccountId: string | null; searchId: string | null }>({
        memberAccountId: null,
        searchId: null,
    });
    const [refreshes, setRefreshes] = useState(0);
    const [result, setResult] = useState<Inspecting>({ kind: 'loading' });
    const { memberAccountId, searchId } = resolveSelection(household, picked);
    const canInspect = household.homeFeedOn && memberAccountId !== null && searchId !== null;

    useEffect(() => {
        if (!canInspect) return;
        let current = true;
        setResult({ kind: 'loading' });
        inspect(memberAccountId, searchId).then(
            (outcome) => {
                if (current) setResult(outcome);
            },
            () => {
                if (current) setResult({ kind: 'error' });
            },
        );
        // A newer pick or refresh supersedes this call; its answer must not overwrite the newer one.
        return () => {
            current = false;
        };
    }, [canInspect, inspect, memberAccountId, searchId, refreshes]);

    if (!household.homeFeedOn) {
        return (
            <PanelCard>
                <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                        <Badge tone="warning">Off</Badge>
                        <Typography variant="body-md" testID="home-feed-off">
                            The Home feed is off for this household.
                        </Typography>
                    </InlineStack>
                    <Typography variant="body-sm" tone="text-secondary">
                        Nothing was evaluated. Turn the HOME_FEED flag on for this vault (ring or override) to
                        inspect it.
                    </Typography>
                    {reloadFailed ? <ReloadError onRetry={onReloadHousehold} /> : null}
                    <InlineStack>
                        <Button variant="tertiary" size="slim" onPress={() => onReloadHousehold?.()}>
                            Check again
                        </Button>
                    </InlineStack>
                </BlockStack>
            </PanelCard>
        );
    }
    if (memberAccountId === null || searchId === null) {
        return (
            <PanelCard>
                <Note testID="home-feed-nothing-to-inspect">
                    {memberAccountId === null
                        ? 'This household has no members to inspect.'
                        : 'This household has no Searches to inspect.'}
                </Note>
            </PanelCard>
        );
    }

    const memberLabel = (accountId: string) =>
        household.members.some((m) => m.accountId === accountId)
            ? (memberEmails?.get(accountId) ?? accountId)
            : `${accountId} (no longer in household)`;
    const memberOptions = household.members.map((member) => ({
        value: member.accountId,
        label: `${memberLabel(member.accountId)} · ${member.role}`,
    }));
    const searchOptions = household.searches.map((search) => ({
        value: search.searchId,
        label: `${search.name} · ${search.stage}${search.isDefault ? ' (default)' : ''}`,
    }));
    const refreshing = result.kind === 'loading';

    return (
        <PanelCard>
            <BlockStack gap="400">
                <InlineStack gap="400" blockAlign="end" wrap>
                    <Select
                        label="Member"
                        options={memberOptions}
                        value={memberAccountId}
                        onChange={(value) => setPicked({ memberAccountId: value, searchId })}
                    />
                    <Select
                        label="Search"
                        options={searchOptions}
                        value={searchId}
                        onChange={(value) => setPicked({ memberAccountId, searchId: value })}
                    />
                </InlineStack>

                {reloadFailed ? <ReloadError onRetry={onReloadHousehold} /> : null}

                <InspectionResult result={result} memberLabel={memberLabel} />

                <InlineStack align="flex-end">
                    <Button
                        variant="tertiary"
                        size="slim"
                        disabled={refreshing}
                        onPress={() => {
                            // The directory too, so a newly added member or Search shows up.
                            onReloadHousehold?.();
                            setRefreshes((count) => count + 1);
                        }}
                    >
                        Refresh
                    </Button>
                </InlineStack>
            </BlockStack>
        </PanelCard>
    );
}

function InspectionResult({
    result,
    memberLabel,
}: {
    result: Inspecting;
    memberLabel: (accountId: string) => string;
}) {
    switch (result.kind) {
        case 'loading':
            return (
                <InlineStack gap="200" blockAlign="center">
                    <Spinner size="small" />
                    <Note testID="home-feed-evaluating">Evaluating…</Note>
                </InlineStack>
            );
        case 'not-found':
            return (
                <Note testID="home-feed-inspection-not-found">
                    That member or Search isn&apos;t in this household any more. Reload the page to refresh the
                    pickers.
                </Note>
            );
        case 'busy':
            return (
                <Note testID="home-feed-inspection-busy">
                    An inspection is already running. Try again in a moment.
                </Note>
            );
        case 'error':
            return (
                <Note testID="home-feed-inspection-error">
                    Couldn&apos;t evaluate the Home feed. Refresh to try again.
                </Note>
            );
        case 'ok': {
            const { inspection } = result;
            if (!inspection.homeFeedOn) {
                return (
                    <Typography variant="body-md" testID="home-feed-inspection-off">
                        The Home feed is off for this household. Nothing was evaluated.
                    </Typography>
                );
            }
            const when = formatUkAndUtc(inspection.evaluatedAt);
            return (
                <BlockStack gap="300">
                    <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                        Evaluated at
                    </Typography>
                    <Typography variant="body-md" testID="home-feed-evaluated-at">
                        {`${when.uk} (${when.utc})`}
                    </Typography>
                    {inspection.questions ? (
                        <HomeFeedQuestions questions={inspection.questions} evaluatedAt={inspection.evaluatedAt} />
                    ) : null}
                    {inspection.inferences ? <HomeFeedInferences inferences={inspection.inferences} /> : null}
                    {inspection.widening ? <HomeFeedWidening widening={inspection.widening} /> : null}
                    {inspection.wildcards ? <HomeFeedWildcards wildcards={inspection.wildcards} /> : null}
                    {inspection.resultsChanged ? (
                        <HomeFeedResultsChanged results={inspection.resultsChanged} />
                    ) : null}
                    {inspection.engagement ? <HomeFeedEngagement engagement={inspection.engagement} /> : null}
                    {inspection.preferences ? (
                        <HomeFeedPreferences preferences={inspection.preferences} memberLabel={memberLabel} />
                    ) : null}
                </BlockStack>
            );
        }
    }
}
